window.Arima = window.Arima || {};
window.Arima.money = (cents, currency = 'USD') => new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency}).format(cents/100);

window.Arima.initImages = (root = document) => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  root.querySelectorAll('img[loading="lazy"]:not([data-arima-image-init])').forEach((image) => {
    image.dataset.arimaImageInit = 'true';
    if (image.complete) return;
    image.classList.add('arima-image-pending');
    const reveal = () => {
      image.classList.remove('arima-image-pending');
      image.classList.add('arima-image-ready');
    };
    image.addEventListener('load', reveal, { once: true });
    image.addEventListener('error', reveal, { once: true });
  });
};
document.addEventListener('DOMContentLoaded', () => window.Arima.initImages());
document.addEventListener('shopify:section:load', (event) => window.Arima.initImages(event.target));

// One owner for modal focus and scroll state across menu, search, cart and sizing.
window.ArimaDialog = (() => {
  let active = null, returnTo = null, previousOverflow = '';
  const focusable = (dialog) => [...dialog.querySelectorAll('a[href],button,input,select,textarea,[tabindex]:not([tabindex="-1"])')].filter(el => !el.disabled && !el.hidden && el.tabIndex >= 0 && el.getClientRects().length);
  const close = (dialog = active, restoreFocus = true) => {
    if (!dialog || dialog !== active) return;
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
    dialog.hidden = true;
    dialog.dispatchEvent(new CustomEvent('arima:dialog-close'));
    active = null;
    document.body.style.overflow = previousOverflow;
    if (restoreFocus && returnTo?.isConnected) returnTo.focus();
    returnTo = null;
  };
  const open = (dialog, trigger = document.activeElement) => {
    if (!dialog || active === dialog) return;
    if (active) close(active, false);
    previousOverflow = document.body.style.overflow;
    active = dialog;
    returnTo = trigger;
    dialog.hidden = false;
    dialog.setAttribute('aria-modal', 'true');
    if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.showModal();
    document.body.style.overflow = 'hidden';
    dialog.dispatchEvent(new CustomEvent('arima:dialog-open'));
    const target = dialog.querySelector('[data-arima-dialog-focus], [autofocus]') || focusable(dialog)[0] || dialog;
    if (target === dialog) dialog.setAttribute('tabindex', '-1');
    target.focus();
  };
  document.addEventListener('keydown', event => {
    if (!active) return;
    if (event.key === 'Escape') { event.preventDefault(); close(); }
    if (event.key !== 'Tab') return;
    const items = focusable(active), first = items[0], last = items.at(-1);
    if (!first) { event.preventDefault(); active.focus(); return; }
    if (event.shiftKey && (document.activeElement === first || !active.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || !active.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
  });
  document.addEventListener('cancel', event => { if (event.target === active) { event.preventDefault(); close(); } }, true);
  return {open, close, get active() { return active; }};
})();


(() => {
  const initialized = new WeakMap();
  const setup = (element) => {
    const controller = new AbortController();
    const cleanups = [];
    initialized.set(element, () => {
      controller.abort();
      cleanups.forEach((cleanup) => cleanup());
      initialized.delete(element);
    });
    return {
      listen: (target, type, callback) => target?.addEventListener(type, callback, { signal: controller.signal }),
      cleanup: (callback) => cleanups.push(callback)
    };
  };
  function initChrome(root = document) {
    root.querySelectorAll('[data-arima-header]').forEach((header) => {
      if (initialized.has(header)) return;
      const { listen, cleanup } = setup(header);
      const openButton = header.querySelector('[data-arima-menu-open]');
      const closeButton = header.querySelector('[data-arima-menu-close]');
      const drawer = header.querySelector('.arima-header__mobile-menu');
      const scrim = header.querySelector('[data-arima-menu-scrim]');
      const syncMenu = () => {
        const expanded = Boolean(drawer && !drawer.hidden);
        if (scrim) scrim.hidden = !expanded;
        openButton?.setAttribute('aria-expanded', String(expanded));
      };
      const close = () => { if (drawer && !drawer.hidden) window.ArimaDialog?.close(drawer); };
      const open = () => { if (drawer) window.ArimaDialog?.open(drawer, openButton); };
      listen(openButton, 'click', open);
      listen(closeButton, 'click', close);
      listen(scrim, 'click', close);
      listen(drawer, 'arima:dialog-open', syncMenu);
      listen(drawer, 'arima:dialog-close', syncMenu);
      const mobile = matchMedia('(max-width: 749px)');
      listen(mobile, 'change', () => { if (!mobile.matches) close(); });
      cleanup(close);

      const navigation = header.querySelector('.arima-header');
      let scrollFrame = 0, revealTimer;
      const syncSticky = () => {
        scrollFrame = 0;
        const fixed = window.scrollY > 250;
        if (fixed === navigation.classList.contains('is-fixed')) return;
        clearTimeout(revealTimer);
        navigation.classList.toggle('is-fixed', fixed);
        navigation.classList.remove('is-revealed');
        if (fixed) {
          if (matchMedia('(prefers-reduced-motion: reduce)').matches) navigation.classList.add('is-revealed');
          else revealTimer = setTimeout(() => navigation.classList.add('is-revealed'), 100);
        }
      };
      listen(window, 'scroll', () => { if (!scrollFrame) scrollFrame = requestAnimationFrame(syncSticky); });
      syncSticky();
      cleanup(() => { cancelAnimationFrame(scrollFrame); clearTimeout(revealTimer); });
      header.querySelectorAll('[data-arima-submenu-toggle]').forEach((button) => {
        listen(button, 'click', () => {
          const submenu = document.getElementById(button.getAttribute('aria-controls'));
          const expanded = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', String(!expanded));
          submenu.hidden = expanded;
        });
      });
      header.querySelectorAll('.arima-header__nav-toggle').forEach((button) => {
        const parent = button.closest('.arima-header__nav-item');
        listen(button, 'click', () => {
          const expanded = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', String(!expanded));
          parent.classList.toggle('is-open', !expanded);
        });
        listen(parent, 'focusout', (event) => {
          if (!parent.contains(event.relatedTarget)) { button.setAttribute('aria-expanded', 'false'); parent.classList.remove('is-open'); }
        });
      });
      const announcement = header.querySelector('[data-arima-announcement]');
      if (announcement) {
        const items = [...announcement.querySelectorAll('[data-arima-announcement-item]')];
        const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
        let index = Math.max(0, items.findIndex((item) => !item.hidden));
        let timer, hovered = false, focused = false, editing = false;
        const stop = () => { clearInterval(timer); timer = null; };
        const show = (next) => {
          items[index].hidden = true;
          index = next;
          items[index].hidden = false;
        };
        const sync = () => {
          stop();
          if (items.length > 1 && !reducedMotion.matches && !hovered && !focused && !editing && !document.hidden) {
            timer = setInterval(() => show((index + 1) % items.length), Number(announcement.dataset.interval) || 6000);
          }
        };
        listen(announcement, 'mouseenter', () => { hovered = true; sync(); });
        listen(announcement, 'mouseleave', () => { hovered = false; sync(); });
        listen(announcement, 'focusin', () => { focused = true; sync(); });
        listen(announcement, 'focusout', (event) => { focused = announcement.contains(event.relatedTarget); sync(); });
        listen(reducedMotion, 'change', sync);
        listen(document, 'visibilitychange', sync);
        listen(header, 'shopify:block:select', (event) => {
          const selected = items.findIndex((item) => item === event.target || item.contains(event.target));
          if (selected < 0) return;
          editing = true;
          show(selected);
          sync();
        });
        listen(header, 'shopify:block:deselect', () => { editing = false; sync(); });
        cleanup(stop);
        sync();
      }
    });
    root.querySelectorAll('[data-arima-footer]').forEach((footer) => {
      if (initialized.has(footer)) return;
      const { listen } = setup(footer);
      const details = footer.querySelector('[data-arima-newsletter-details]');
      if (!details) return;
      const mobile = matchMedia('(max-width: 749px)');
      const response = details.querySelector('.arima-footer__form-message');
      const sync = () => { if (!mobile.matches || response) details.open = true; else details.open = false; };
      sync();
      listen(mobile, 'change', sync);
      footer.querySelector('[data-arima-localization]')?.querySelectorAll('select').forEach((select) => listen(select, 'change', () => select.form.submit()));
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initChrome()); else initChrome();
  document.addEventListener('shopify:section:load', (event) => initChrome(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-arima-header], [data-arima-footer]').forEach((element) => initialized.get(element)?.());
  });
})();


(() => {
  function initializeFaq(root) {
    root.querySelectorAll('.arima-editorial-faq__question').forEach((button) => {
      if (button.dataset.arimaFaqReady === 'true') return;
      button.dataset.arimaFaqReady = 'true';
      button.addEventListener('click', () => {
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        if (!panel) return;
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        panel.hidden = expanded;
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeFaq(document));
  else initializeFaq(document);
  document.addEventListener('shopify:section:load', (event) => initializeFaq(event.target));
})();


(function(){
  function initCatalog(root){
    (root || document).querySelectorAll('[data-arima-catalog]').forEach(function(catalog){
      if(catalog.dataset.arimaReady) return;
      catalog.dataset.arimaReady='true';
      if(catalog.dataset.arimaDefaultSort){var currentUrl=new URL(window.location.href);if(!currentUrl.searchParams.has('sort_by')){currentUrl.searchParams.set('sort_by',catalog.dataset.arimaDefaultSort);window.location.replace(currentUrl.toString());return;}}
      var filters=catalog.querySelector('[data-arima-filters]');
      var open=catalog.querySelector('[data-arima-filter-open]');
      var mobile=matchMedia('(max-width: 749px)');
      function syncPanel(){
        if(!filters)return;
        if(mobile.matches){
          filters.setAttribute('role','dialog');filters.setAttribute('aria-modal','true');
          filters.setAttribute('aria-label','Filters');filters.hidden=!filters.classList.contains('is-open');
        }else{
          if(filters.classList.contains('is-open'))closePanel();
          filters.hidden=false;filters.removeAttribute('role');filters.removeAttribute('aria-modal');
        }
      }
      function closePanel(){if(filters&&filters.classList.contains('is-open')){window.ArimaDialog.close(filters);filters.classList.remove('is-open');syncPanel();}}
      if(open&&filters){
        syncPanel();mobile.addEventListener('change',syncPanel);
        open.addEventListener('click',function(){if(!window.ArimaDialog)return;filters.classList.add('is-open');window.ArimaDialog.open(filters,open);});
        filters.querySelectorAll('[data-arima-filter-close]').forEach(function(button){button.addEventListener('click',closePanel);});
        filters.addEventListener('arima:dialog-close',function(){filters.classList.remove('is-open');syncPanel();});
      }
      catalog.querySelectorAll('[data-arima-sort]').forEach(function(select){select.addEventListener('change',function(){var url=new URL(window.location.href);url.searchParams.set('sort_by',select.value);url.searchParams.delete('page');window.location.href=url.toString();});});
    });
    (root || document).querySelectorAll('[data-arima-predictive]').forEach(function(wrapper){
      if(wrapper.dataset.arimaReady) return;wrapper.dataset.arimaReady='true';
      var input=wrapper.querySelector('[data-arima-predictive-input]');var panel=wrapper.querySelector('[data-arima-predictive-results]');var timer,controller;
      function hide(){panel.hidden=true;input.setAttribute('aria-expanded','false');panel.replaceChildren();}
      var searchUrl=wrapper.dataset.searchUrl||'/search';
      function resultsUrl(query){var url=new URL(searchUrl,window.location.origin);url.searchParams.set('q',query);url.searchParams.set('type','product');return url.pathname+url.search;}
      function link(url,label,cls){var a=document.createElement('a');a.href=url;a.textContent=label;if(cls)a.className=cls;return a;}
      function render(data,query){var results=data.resources&&data.resources.results||{};var products=results.products||[];var suggestions=results.queries||[];panel.replaceChildren();if(!products.length&&!suggestions.length){hide();return;}
        var columns=document.createElement('div');columns.className='arima-search__suggestion-columns';if(!suggestions.length||!products.length)columns.classList.add('arima-search__suggestion-columns--single');var terms=document.createElement('div');var termHeading=document.createElement('h2');termHeading.textContent='Suggestions';terms.append(termHeading);suggestions.forEach(function(item){terms.append(link(item.url||resultsUrl(item.text),item.text,'arima-search__suggestion-query'));});if(suggestions.length)columns.append(terms);var list=document.createElement('div');var productHeading=document.createElement('h2');productHeading.textContent='Products';list.append(productHeading);products.forEach(function(item){var a=link(item.url,'','arima-search__suggestion-product');var title=document.createElement('span');title.textContent=item.title;a.append(title);var src=item.featured_image&&item.featured_image.url;if(src){var img=document.createElement('img');img.src=src;img.alt='';img.loading='lazy';a.prepend(img);}list.append(a);});if(products.length)columns.append(list);panel.append(columns);panel.append(link(resultsUrl(query),'Show all results for “'+query+'” →','arima-search__show-all'));panel.hidden=false;input.setAttribute('aria-expanded','true');}
      wrapper.addEventListener('arima:dialog-close',function(){clearTimeout(timer);if(controller)controller.abort();hide();});
      input.addEventListener('input',function(){clearTimeout(timer);var query=input.value.trim();if(controller)controller.abort();if(query.length<2){hide();return;}timer=setTimeout(function(){controller=new AbortController();var path=wrapper.dataset.predictiveUrl||'/search/suggest';fetch(path.replace(/\.json$/, '')+'.json?q='+encodeURIComponent(query)+'&resources[type]=product,query&resources[limit]=4',{signal:controller.signal}).then(function(response){if(!response.ok)throw Error('Predictive search unavailable');return response.json();}).then(function(data){if(input.value.trim()===query)render(data,query);}).catch(function(error){if(error.name!=='AbortError')hide();});},180);});
      input.addEventListener('keydown',function(event){if(event.key==='Escape')hide();if(event.key==='ArrowDown'&&!panel.hidden){var first=panel.querySelector('a');if(first){event.preventDefault();first.focus();}}});document.addEventListener('click',function(event){if(!wrapper.contains(event.target))hide();});
    });
  }
  document.addEventListener('click',function(event){
    var trigger=event.target.closest('[data-arima-search-open]');
    if(!trigger||event.defaultPrevented||event.button!==0||event.metaKey||event.ctrlKey||event.shiftKey||event.altKey)return;
    var dialog=document.querySelector('[data-arima-search-dialog]');
    if(!dialog||!window.ArimaDialog)return;
    event.preventDefault();var header=document.querySelector('.arima-header');if(header)dialog.style.setProperty('--arima-search-header-height',header.getBoundingClientRect().height+'px');window.ArimaDialog.open(dialog,trigger);
  });
  document.addEventListener('click',function(event){var close=event.target.closest('[data-arima-search-close]');if(close&&window.ArimaDialog){window.ArimaDialog.close(close.closest('[data-arima-search-dialog]'));}});
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',function(){initCatalog(document);});else initCatalog(document);
  document.addEventListener('shopify:section:load',function(event){initCatalog(event.target);});
})();


(() => {
  const root = window.Shopify?.routes?.root || '/';
  const money = (cents, currency) => window.Arima.money(cents, currency || window.Shopify?.currency?.active || 'USD');
  const safe = value => String(value ?? '').replace(/[&<>"']/g, char => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[char]));
  function initProduct(section) {
    if (section.dataset.arimaReady) return;
    section.dataset.arimaReady = 'true';
    let product;
    try { product = JSON.parse(section.dataset.productJson); } catch { return; }
    const displayPrices = JSON.parse(section.dataset.displayPrices || '{}');
    const form = section.querySelector('[data-arima-product-form]');
    const track = section.querySelector('[data-gallery-track]');
    const slides = [...section.querySelectorAll('.arima-product__slide')];
    const chooseImage = id => {
      const slide = slides.find(node => String(node.dataset.mediaId) === String(id));
      if (!slide) return;
      track.scrollTo({left: slide.offsetLeft - track.offsetLeft, behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      section.querySelectorAll('[data-media-id]').forEach(node => {
        const active = String(node.dataset.mediaId) === String(id);
        node.classList.toggle('is-active', active);
        if (node.matches('.arima-product__thumb')) node.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (node.matches('.arima-product__dots button')) node.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };
    section.querySelectorAll('button[data-media-id]').forEach(button => button.addEventListener('click', () => chooseImage(button.dataset.mediaId)));
    let scrollTimer;
    track?.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const slide = slides.reduce((closest, candidate) => Math.abs(candidate.offsetLeft - track.offsetLeft - track.scrollLeft) < Math.abs(closest.offsetLeft - track.offsetLeft - track.scrollLeft) ? candidate : closest, slides[0]);
        if (slide) section.querySelectorAll('.arima-product__dots button').forEach(dot => dot.setAttribute('aria-current', String(dot.dataset.mediaId) === String(slide.dataset.mediaId) ? 'true' : 'false'));
      }, 80);
    });
    const zoomDialog = document.querySelector('[data-zoom-dialog]');
    section.querySelector('[data-gallery-zoom]')?.addEventListener('click', () => {
      const current = slides.reduce((closest, candidate) => Math.abs(candidate.offsetLeft - track.offsetLeft - track.scrollLeft) < Math.abs(closest.offsetLeft - track.offsetLeft - track.scrollLeft) ? candidate : closest, slides[0]);
      const image = current?.querySelector('img');
      if (!image || !zoomDialog) return;
      const large = zoomDialog.querySelector('img');
      large.src = image.currentSrc || image.src;
      large.alt = image.alt;
      window.ArimaDialog.open(zoomDialog, section.querySelector('[data-gallery-zoom]'));
    });
    zoomDialog?.querySelector('[data-zoom-close]')?.addEventListener('click', () => window.ArimaDialog.close(zoomDialog));
    const updateVariant = () => {
      const selections = [...form.querySelectorAll('.arima-product__option')].map(group => group.querySelector('input:checked')?.value);
      const variant = product.variants.find(item => item.options.every((option, index) => option === selections[index])) || product.variants[0];
      if (!variant) return;
      form.querySelector('[data-variant-id]').value = variant.id;
      const price = section.querySelector('[data-product-prices]');
      const displayPrice = displayPrices[variant.id] ?? variant.price;
      const comparePrice = Math.max(variant.compare_at_price || 0, variant.price);
      const compare = comparePrice > displayPrice ? '<s>' + money(comparePrice, section.dataset.currency) + '</s>' : '';
      const savings = comparePrice > displayPrice ? '<em>Save ' + money(comparePrice - displayPrice, section.dataset.currency) + '</em>' : '';
      price.innerHTML = compare + '<span>' + money(displayPrice, section.dataset.currency) + '</span>' + savings;
      const button = form.querySelector('[data-add-button]');
      button.disabled = !variant.available;
      button.textContent = variant.available ? 'Add to cart' : 'Sold out';
      section.querySelector('[data-stock]').innerHTML = variant.available ? '<span aria-hidden="true">●</span> ' + safe(section.dataset.stockText) : 'Sold out';
      history.replaceState({}, '', location.pathname + '?variant=' + variant.id);
      if (variant.featured_media?.id || variant.featured_image?.id) chooseImage(variant.featured_media?.id || variant.featured_image?.id);
  };
    form?.addEventListener('change', updateVariant);
    if (section.dataset.selectedMedia) requestAnimationFrame(() => chooseImage(section.dataset.selectedMedia));
    form?.addEventListener('submit', async event => {
      event.preventDefault();
      const error = form.querySelector('[data-product-error]');
      error.hidden = true;
      const button = form.querySelector('[data-add-button]');
      button.disabled = true;
      try {
        const response = await fetch(root + 'cart/add.js', {method:'POST', headers:{Accept:'application/json'}, body:new FormData(form)});
        const payload = await response.json();
        if (!response.ok) throw Error(payload.description || 'Could not add this item.');
        await refreshDrawer();
        openDrawer(button);
      } catch (issue) {
        error.textContent = issue instanceof TypeError ? 'Could not add this item. Please try again.' : issue.message;
        error.hidden = false;
      } finally { button.disabled = button.textContent === 'Sold out'; }
  });
  }
  const drawer = () => document.querySelector('[data-cart-drawer]');
  let cartBusy = false;
  function openDrawer(trigger) {
    const panel = drawer();
    if (!panel) return;
    window.ArimaDialog.open(panel, trigger);
  }
  function closeDrawer() { if (drawer()) window.ArimaDialog.close(drawer()); }
  function cartError(message) {
    const error = drawer()?.querySelector('[data-cart-error]');
    if (error) { error.hidden = !message; error.textContent = message || ''; }
  }
  async function refreshDrawer() {
    const [response, itemsResponse] = await Promise.all([
      fetch(root + 'cart.js', {headers:{Accept:'application/json'}}),
      fetch(root + '?sections=arima-cart-drawer-items', {headers:{Accept:'application/json'}})
    ]);
    if (!response.ok || !itemsResponse.ok) throw Error('Could not load cart. Please try again.');
    const [cart, sections] = await Promise.all([response.json(), itemsResponse.json()]);
    if (typeof sections['arima-cart-drawer-items'] !== 'string') throw Error('Could not load cart. Please try again.');
    const items = new DOMParser().parseFromString(sections['arima-cart-drawer-items'], 'text/html');
    const subtotal = items.querySelector('[data-cart-subtotal-markup]');
    if (!subtotal) throw Error('Could not load cart. Please try again.');
    const panel = drawer();
    document.querySelectorAll('[data-arima-cart-open], .arima-header__cart').forEach(headerCart => {
      headerCart.setAttribute('aria-label', 'Cart, ' + cart.item_count + (cart.item_count === 1 ? ' item' : ' items'));
      let count = headerCart.querySelector('.arima-header__cart-count');
      if (cart.item_count > 0) { if (!count) { count = document.createElement('span'); count.className = 'arima-header__cart-count'; headerCart.append(count); } count.textContent = cart.item_count; }
      else count?.remove();
    });
    if (!panel) return;
    panel.querySelector('[data-cart-subtotal]').innerHTML = subtotal.innerHTML;
    subtotal.remove();
    panel.querySelector('[data-cart-bottom]').hidden = !cart.item_count;
    panel.querySelector('[data-cart-items]').innerHTML = items.body.innerHTML;
    document.dispatchEvent(new CustomEvent('arima:cart-updated', {detail:cart}));
  }
  async function changeQuantity(control) {
    if (cartBusy) return;
    const item = control.closest('[data-line]');
    const input = item?.querySelector('input[type=number]');
    if (!item || !input) return;
    const quantity = control.hasAttribute('data-cart-remove') ? 0 : Math.max(0, Math.floor(Number(input.value) + Number(control.dataset.cartQuantity || 0)));
    if (!Number.isFinite(quantity)) return;
    cartBusy = true;
    const panel = item.closest('[data-cart-drawer]');
    const controls = [...item.querySelectorAll('button,input')];
    controls.forEach(node => { node.disabled = true; });
    cartError('');
    try {
      const response = await fetch(root + 'cart/change.js', {method:'POST', headers:{'Content-Type':'application/json',Accept:'application/json'}, body:JSON.stringify(item.dataset.key ? {id:item.dataset.key,quantity} : {line:Number(item.dataset.line),quantity})});
      const result = await response.json();
      if (!response.ok) throw Error(result.description || 'Could not update cart.');
      if (panel) {
        const line = item.dataset.line;
        const focus = control.hasAttribute('data-cart-remove') ? '[data-cart-close]' : '[data-line="' + line + '"] ' + (control.hasAttribute('data-cart-input') ? '[data-cart-input]' : '[data-cart-quantity="' + control.dataset.cartQuantity + '"]');
        await refreshDrawer();
        (panel.querySelector(focus) || panel.querySelector('[data-cart-close]')).focus();
      } else location.reload();
    } catch (issue) {
      const message = issue instanceof TypeError ? 'Could not update your cart. Please try again.' : issue.message;
      if (panel) cartError(message); else alert(message);
      controls.forEach(node => { node.disabled = false; });
    } finally { cartBusy = false; }
  }
  async function initRecommendations(section) {
    if (section.dataset.ready || section.querySelector('.arima-product-card')) return;
    section.dataset.ready = 'true';
    try {
      const response = await fetch(section.dataset.recommendationUrl);
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = html.querySelector('[data-recommendation-items]');
      if (items?.children.length) section.querySelector('[data-recommendation-items]').innerHTML = items.innerHTML;
      else section.hidden = true;
    } catch { section.hidden = true; }
  }
  async function initRecent(section) {
    if (section.dataset.ready) return;
    section.dataset.ready = 'true';
    try {
      const handles = JSON.parse(localStorage.getItem('arima-recent-products') || '[]');
      const previous = handles.filter(handle => typeof handle === 'string' && /^[a-z0-9-]+$/.test(handle) && handle !== section.dataset.currentHandle).slice(0, Number(section.dataset.recentLimit));
      localStorage.setItem('arima-recent-products', JSON.stringify([section.dataset.currentHandle, ...previous].filter(Boolean).slice(0, 8)));
      const cards = (await Promise.all(previous.map(async handle => {
        const response = await fetch(root + 'products/' + encodeURIComponent(handle) + '?section_id=arima-recent-card');
        if (!response.ok) return '';
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        return html.querySelector('.arima-product-card')?.outerHTML || '';
      }))).filter(Boolean);
      section.querySelector('[data-recent-items]').innerHTML = cards.join('');
      section.hidden = !cards.length;
    } catch { /* A blocked storage provider leaves the optional history section hidden. */ }
  }
  function init() {
    document.querySelectorAll('[data-arima-product]').forEach(initProduct);
    document.querySelectorAll('[data-arima-recommendations]').forEach(initRecommendations);
    document.querySelectorAll('[data-arima-recent]').forEach(initRecent);
    if (document.body.dataset.arimaCartReady) return;
    document.body.dataset.arimaCartReady = 'true';
    document.addEventListener('click', async event => {
      const sizeOpener = event.target.closest('[data-open-size]');
      if (sizeOpener) {
        const dialog = sizeOpener.closest('.shopify-section')?.querySelector('[data-size-dialog]') || document.querySelector('[data-size-dialog]');
        if (dialog) { event.preventDefault(); window.ArimaDialog.open(dialog, sizeOpener); }
        return;
      }
      const sizeClose = event.target.closest('[data-close-size]');
      if (sizeClose) { window.ArimaDialog.close(sizeClose.closest('[data-size-dialog]')); return; }
      const unit = event.target.closest('[data-size-unit]');
      if (unit) {
        const dialog = unit.closest('[data-size-dialog]');
        dialog.querySelectorAll('[data-size-unit]').forEach(tab => tab.classList.toggle('is-active', tab === unit));
        dialog.dataset.unit = unit.dataset.sizeUnit;
        dialog.querySelectorAll('[data-measurement]').forEach(cell => { cell.textContent = unit.dataset.sizeUnit === 'cm' ? cell.dataset.measurementCm : cell.dataset.measurement; });
        return;
      }
      if (event.target.matches('[data-size-dialog]')) {
        const box = event.target.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) window.ArimaDialog.close(event.target);
      }

      const opener = event.target.closest('[data-arima-cart-open]');
      if (opener && drawer() && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
        event.preventDefault(); openDrawer(opener); cartError('');
        drawer().setAttribute('aria-busy', 'true');
        try { await refreshDrawer(); } catch (issue) { cartError(issue instanceof TypeError ? 'Could not load your cart. Please try again.' : issue.message); }
        finally { drawer().removeAttribute('aria-busy'); }
      }
      const quantity = event.target.closest('[data-cart-quantity], [data-cart-remove]');
      if (quantity) changeQuantity(quantity);
      if (event.target.closest('[data-cart-close]')) closeDrawer();
      if (event.target === drawer()) {
        const box = drawer().getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeDrawer();
      }
    });
    document.addEventListener('change', event => { if (event.target.matches('[data-cart-input]')) changeQuantity(event.target); });
  }
  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();


document.addEventListener('click', event => {
  const button = event.target.closest('[data-address-delete]');
  if (!button || !confirm('Delete this address?')) return;
  const form = document.createElement('form');
  form.method = 'post'; form.action = button.dataset.addressDelete;
  form.innerHTML = '<input type="hidden" name="_method" value="delete">';
  document.body.append(form); form.submit();
});

